import { UpdateProject } from 'modules/domains';
import { useMutation, UseMutationOptions } from 'react-query';
import { ApiMethod, ApiResponse, get, post, postFormRaw } from 'services/api';
import {
  RestQueryHookOptions,
  useRestMutation,
  UseRestMutationOptions,
  useRestQuery,
} from 'utils/hooks/api';

export const PROJECTS_URL = '/settings/projects.json';
export const UPDATE_PROJECT_URL = '/settings/project/update';
export const PROJECT_JSON_URL = '/settings/project.json';
export const updateProjectLogoUrl = (projectId: number) => `/project/${projectId}/logo`;
export const updateProjectJsonUrl = (projectId: number) => `/project/${projectId}/settings.json`;
export const CREATE_PROJECT_JSON_URL = '/settings/createProjectJson';
export const PROJECT_TIME_ZONES_URL = '/settings/projects/allowedTimeZones';
export const UPDATE_CUSTOM_MTA_ESP_LABEL_URL = '/settings/updateCustomMTAESPLabel';
export const ADD_PINNED_PROJECT_URL = '/i/preferences/addPinnedProject.json';
export const REMOVE_PINNED_PROJECT_URL = '/i/preferences/removePinnedProject.json';

/**
 * Fetch the Projects data
 */
export const getProjects = () => get(PROJECTS_URL);

/**
 * Update project
 */
export const updateProject = (project: Record<string, any>) => post(UPDATE_PROJECT_URL, project);

export const useUpdateProject = (projectId: number, options?: UseRestMutationOptions) => {
  const url = updateProjectJsonUrl(projectId);
  return useRestMutation<unknown, UpdateProject>(url, ApiMethod.post, options);
};

export const updateProjectLogo = (projectId: number, formData: FormData) => {
  const url = updateProjectLogoUrl(projectId);
  return postFormRaw(url, formData);
};

export const useUpdateProjectLogo = (projectId: number, options?: UseMutationOptions) =>
  useMutation({
    mutationFn: (formData: FormData) => postFormRaw(updateProjectLogoUrl(projectId), formData),
    ...options,
  } as UseMutationOptions<ApiResponse, unknown, unknown>);

export const useCurrentProject = (options?: RestQueryHookOptions) =>
  useRestQuery(PROJECT_JSON_URL, {}, options);

export const useProjectTimeZones = (options?: RestQueryHookOptions) =>
  useRestQuery(PROJECT_TIME_ZONES_URL, {}, options);

export const useCreateProjectJson = (options?: UseRestMutationOptions) =>
  useRestMutation(CREATE_PROJECT_JSON_URL, ApiMethod.post, options);

export const useUpdateCustomMtaLabel = (options?: UseRestMutationOptions) =>
  useRestMutation(UPDATE_CUSTOM_MTA_ESP_LABEL_URL, ApiMethod.post, options);

export const useAddPinnedProject = (options?: UseRestMutationOptions) =>
  useRestMutation<unknown, { pinnedProject: number }>(
    ADD_PINNED_PROJECT_URL,
    ApiMethod.post,
    options,
  );

export const useRemovePinnedProject = (options?: UseRestMutationOptions) =>
  useRestMutation<unknown, { pinnedProject: number }>(
    REMOVE_PINNED_PROJECT_URL,
    ApiMethod.post,
    options,
  );
